import { comboKey } from "~/lib/variant-matrix";
import type { PdpOption, PdpVariant } from "~/server/api/schemas/pdp";

export type VariantSelection = Record<string, string>;

export interface ValueAvailability {
  /** At least one purchasable variant matches this value and the other choices. */
  available: boolean;
  /** Aggregate stock across the matching variants (0 for preorder/backorder). */
  stock: number;
  /** A real variant contains this value, including sold-out/disconnected choices. */
  selectable?: boolean;
  /** Selecting it must clear incompatible choices on another axis. */
  requiresReset?: boolean;
}

/** Product-option-value ids are globally unique, so one flat lookup is enough. */
export type VariantAvailability = Record<string, ValueAvailability>;

function valueToOptionIndex(
  options: readonly PdpOption[],
): Map<string, string> {
  return new Map(
    options.flatMap((option) =>
      option.values.map((value) => [value.id, option.id] as const),
    ),
  );
}

/** Return the option selection represented by a well-formed variant. */
export function selectionForVariant(
  options: readonly PdpOption[],
  variant: PdpVariant | null | undefined,
): VariantSelection {
  if (!variant) return {};

  const optionByValue = valueToOptionIndex(options);
  const selected: VariantSelection = {};
  for (const valueId of variant.optionValueIds) {
    const optionId = optionByValue.get(valueId);
    if (!optionId || selected[optionId]) return {};
    selected[optionId] = valueId;
  }

  return options.every((option) => selected[option.id] !== undefined)
    ? selected
    : {};
}

/** Precompute the canonical combination lookup once per product. */
export function buildVariantLookup(
  variants: readonly PdpVariant[],
): Map<string, PdpVariant> {
  const lookup = new Map<string, PdpVariant>();
  for (const variant of variants) {
    const key = comboKey(variant.optionValueIds);
    if (!lookup.has(key)) lookup.set(key, variant);
  }
  return lookup;
}

/** Resolve only complete selections; partial and impossible combinations fail. */
export function resolveVariant(
  options: readonly PdpOption[],
  variants: readonly PdpVariant[],
  selected: VariantSelection,
  lookup = buildVariantLookup(variants),
): PdpVariant | undefined {
  const valueIds: string[] = [];
  for (const option of options) {
    const valueId = selected[option.id];
    if (!valueId || !option.values.some((value) => value.id === valueId)) {
      return undefined;
    }
    valueIds.push(valueId);
  }

  const variant = lookup.get(comboKey(valueIds));
  return variant?.optionValueIds.length === options.length
    ? variant
    : undefined;
}

/**
 * Availability for a value keeps selections from every *other* option, then
 * asks whether an in-stock variant exists with the candidate value. Excluding
 * the value's own axis lets a shopper switch it without first clearing it.
 */
export function computeAvailability(
  options: readonly PdpOption[],
  variants: readonly PdpVariant[],
  selected: VariantSelection,
): VariantAvailability {
  const optionByValue = valueToOptionIndex(options);
  const availability: VariantAvailability = {};

  for (const option of options) {
    // The other-axis constraints depend only on the option, not each value —
    // compute them once per option instead of once per value.
    const constraints = Object.entries(selected).filter(
      ([optionId]) => optionId !== option.id,
    );
    for (const value of option.values) {
      const candidates = variants.filter(variant => variant.optionValueIds.includes(value.id) && Object.keys(selectionForVariant(options, variant)).length === options.length);
      const matching = candidates.filter(variant => constraints.every(([optionId, valueId]) => optionByValue.get(valueId) === optionId && variant.optionValueIds.includes(valueId)));
      let available = false;
      let stock = 0;
      for (const variant of candidates) {
        const purchasable =
          variant.manageStock === false
            ? variant.manualStockStatus !== "OUT_OF_STOCK"
            : variant.stock > 0 || variant.isPreorder || variant.backorderable;
        if (!purchasable || !variant.optionValueIds.includes(value.id)) {
          continue;
        }
        const matchesConstraints = constraints.every(
          ([optionId, valueId]) =>
            optionByValue.get(valueId) === optionId &&
            variant.optionValueIds.includes(valueId),
        );
        if (!matchesConstraints) continue;
        available = true;
        stock += variant.stock;
      }
      availability[value.id] = { available, stock, selectable: candidates.length > 0, requiresReset: candidates.length > 0 && matching.length === 0 };
    }
  }

  return availability;
}

export function firstUnmetOption(
  options: readonly PdpOption[],
  selected: VariantSelection,
): PdpOption | null {
  return options.find((option) => selected[option.id] === undefined) ?? null;
}

/** Clone search params and change only the variant deep-link. */
export function withVariantParam(
  searchParams: Pick<URLSearchParams, "toString">,
  variantId: string | null,
): string {
  const next = new URLSearchParams(searchParams.toString());
  if (variantId) next.set("variant", variantId);
  else next.delete("variant");
  return next.toString();
}


/** Keep the most existing choices compatible with the deliberately chosen value.
 * Never auto-select another axis: the customer confirms the remaining choices. */
export function selectReachableOption(options: readonly PdpOption[], variants: readonly PdpVariant[], selected: VariantSelection, optionId: string, valueId: string): VariantSelection | null {
  if (!options.find(option => option.id === optionId)?.values.some(value => value.id === valueId)) return null;
  const candidates = variants.map(variant => ({variant, selection: selectionForVariant(options, variant)}))
    .filter(candidate => candidate.selection[optionId] === valueId && Object.keys(candidate.selection).length === options.length);
  if (!candidates.length) return null;
  const score = (selection: VariantSelection) => Object.entries(selected).filter(([key, value]) => key !== optionId && selection[key] === value).length;
  candidates.sort((a, b) => score(b.selection) - score(a.selection) || a.variant.id.localeCompare(b.variant.id));
  const target = candidates[0]!.selection;
  return {...Object.fromEntries(Object.entries(selected).filter(([key, value]) => key !== optionId && target[key] === value)), [optionId]: valueId};
}
